import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PostPurchaseService from 'services/PostPurchaseService';
import { useConfirm } from './ConfirmDialog';
import { ExperimentsTab } from './Experiments';
import { OrdersTab } from './OrdersDebug';
import { ShippingTab } from './ShippingMargin';
import { PerformanceTable } from './PerformanceTable';
import { RevenueChart } from './RevenueChart';
import { STOCK_RULE_LABELS, StandardPagePreview, errorText, pacific, usd } from './shared';
import { Period, StaffCustomer, StockRule, UnswappedResponse, Upsell, UpsellPreview, UpsellsResponse } from './types';

// Post-Purchase Upsells: the offers shown on Dosaze between payment and the
// thank-you page. Upsells themselves (products, prices, wording, design) are
// defined in code — sbly-dash-collector src/ecommerce/postPurchase/upsells.js —
// so this page switches them on and off and shows how each design performs.
// Switches reach live checkouts within ~30 seconds.

const PERIODS = [7, 30, 90];
const todayPacific = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
// The Pacific date before `date` (YYYY-MM-DD), counted at noon so no zone shift crosses a day.
const dayBefore = (date: string) => new Date(Date.parse(`${date}T12:00:00Z`) - 86_400_000).toISOString().slice(0, 10);

// Whether a response is for this period (a range or a number of days).
const samePeriod = (response: UpsellsResponse, period: Period) =>
  'days' in period ? response.days === period.days : response.days == null && response.from === period.from && response.to === period.to;

// What the period menu shows: a number of days, today, yesterday, or a range.
type PeriodChoice = number | 'today' | 'yesterday' | 'range';
const singleDay = (choice: 'today' | 'yesterday') => {
  const date = choice === 'today' ? todayPacific() : dayBefore(todayPacific());
  return { from: date, to: date };
};

// Today or yesterday (Pacific), the last 7/30/90 days, or a range of dates:
// the chart and the tables follow it.
function PeriodPicker({ period, onChange }: { period: Period; onChange: (period: Period) => void }) {
  const [choice, setChoice] = React.useState<PeriodChoice>('days' in period ? period.days : 'range');
  const [range, setRange] = React.useState(() => {
    const to = todayPacific();
    const from = new Date(Date.parse(`${to}T12:00:00Z`) - 13 * 86_400_000).toISOString().slice(0, 10);
    return 'days' in period ? { from, to } : { from: period.from, to: period.to };
  });
  const apply = (next: { from: string; to: string }) => {
    setRange(next);
    if (next.from && next.to && next.from <= next.to) onChange(next);
  };
  const choose = (next: PeriodChoice) => {
    setChoice(next);
    if (next === 'range') apply(range);
    else if (next === 'today' || next === 'yesterday') onChange(singleDay(next));
    else onChange({ days: next });
  };
  return (
    <>
      <Select
        size="small"
        value={choice}
        onChange={event => choose(event.target.value as PeriodChoice)}
        labelId="post-purchase-period-label"
        name="period"
      >
        <MenuItem value="today">Today</MenuItem>
        <MenuItem value="yesterday">Yesterday</MenuItem>
        {PERIODS.map(days => (
          <MenuItem key={days} value={days}>Last {days} days</MenuItem>
        ))}
        <MenuItem value="range">Date range…</MenuItem>
      </Select>
      {choice === 'range' && (
        <>
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={range.from}
            inputProps={{ max: range.to }} onChange={event => apply({ ...range, from: event.target.value })} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={range.to}
            inputProps={{ min: range.from, max: todayPacific() }} onChange={event => apply({ ...range, to: event.target.value })} />
        </>
      )}
    </>
  );
}

// The first load's placeholder, in the page's own shape (tabs, the money
// chart, a table) so nothing jumps when the numbers arrive.
const SKELETON_BARS = [38, 62, 45, 80, 30, 70, 55, 90, 48, 66, 74, 58, 84, 52];

function PageSkeleton() {
  return (
    <Stack spacing={3} aria-label="loading upsells" aria-busy="true">
      <Stack direction="row" spacing={3} sx={{ borderBottom: 1, borderColor: 'divider', pb: 1.5 }}>
        {[64, 88, 56].map(width => (
          <Skeleton key={width} variant="text" width={width} height={28} />
        ))}
      </Stack>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Skeleton variant="text" width={180} height={28} />
        <Skeleton variant="text" width="70%" />
        <Stack direction="row" spacing={4} sx={{ my: 2 }}>
          {[0, 1, 2, 3].map(index => (
            <Box key={index}>
              <Skeleton variant="text" width={80} />
              <Skeleton variant="text" width={100} height={36} />
            </Box>
          ))}
        </Stack>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, height: 220, px: 1 }}>
          {SKELETON_BARS.map((height, index) => (
            <Skeleton key={index} variant="rectangular" sx={{ flex: 1, height: `${height}%`, borderRadius: 0.5 }} />
          ))}
        </Box>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2 }}>
        {[0, 1, 2, 3, 4].map(index => (
          <Skeleton key={index} variant="text" height={36} />
        ))}
      </Paper>
    </Stack>
  );
}

const STORE_LABELS: Record<string, string> = { US: 'dosaze.com', DEV: 'Test store' };

function statusOf(upsell: Upsell) {
  if (!upsell.enabled) return { label: 'Off', color: 'default' as const };
  if (upsell.staffOnly) return { label: 'Staff only', color: 'warning' as const };
  return { label: 'Live', color: 'success' as const };
}

type Switches = { enabled?: boolean; staffOnly?: boolean; stockRule?: StockRule };


// What the stock rule decides, and where the target is stocked right now.
function StockCell({ upsell, onChange, saving }: { upsell: Upsell; onChange: (switches: Switches) => void; saving: boolean }) {
  // Missing until the API with the stock check (sbly-dash-collector#948) is live.
  if (!upsell.targetStock) return <Typography variant="caption" color="text.secondary">Needs the API update</Typography>;
  const { centers, error } = upsell.targetStock;
  return (
    <Stack spacing={0.5}>
      <Select
        size="small"
        value={upsell.stockRule}
        disabled={saving}
        inputProps={{ 'aria-label': `${upsell.id} stock check` }}
        onChange={event => onChange({ stockRule: event.target.value as StockRule })}
      >
        {(Object.keys(STOCK_RULE_LABELS) as StockRule[]).map(rule => (
          <MenuItem key={rule} value={rule}>{STOCK_RULE_LABELS[rule]}</MenuItem>
        ))}
      </Select>
      <Typography variant="caption" color={error ? 'error' : 'text.secondary'}>
        {error
          ? `ShipBob stock unavailable: ${error}`
          : centers?.length
            ? `${upsell.target.sku} in stock: ${centers.map(c => `${c.center} ${c.fulfillable}`).join(' · ')}`
            : `${upsell.target.sku} is in no ShipBob center`}
      </Typography>
    </Stack>
  );
}

function UpsellRow({ upsell, onChange, saving }: { upsell: Upsell; onChange: (switches: Switches) => void; saving: boolean }) {
  const status = statusOf(upsell);
  return (
    <TableRow data-upsell-id={upsell.id}>
      <TableCell>{upsell.priority}</TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={600}>{upsell.id}</Typography>
        <Typography variant="caption" color="text.secondary">{upsell.description}</Typography>
      </TableCell>
      <TableCell>
        <Tooltip title={`Layout: ${upsell.layout}. The design id changes whenever the upsell's content does.`}>
          <Typography variant="body2" fontFamily="monospace">{upsell.designId}</Typography>
        </Tooltip>
      </TableCell>
      <TableCell><Chip size="small" label={status.label} color={status.color} /></TableCell>
      <TableCell>
        <Switch
          inputProps={{ 'aria-label': `${upsell.id} enabled` }}
          checked={upsell.enabled}
          disabled={saving}
          // Switching on always starts staff-only: going live for every customer
          // is a second, confirmed step (the Staff only switch).
          onChange={event => onChange(event.target.checked ? { enabled: true, staffOnly: true } : { enabled: false })}
        />
      </TableCell>
      <TableCell>
        <Switch
          inputProps={{ 'aria-label': `${upsell.id} staff only` }}
          checked={upsell.staffOnly}
          disabled={saving}
          onChange={event => onChange({ staffOnly: event.target.checked })}
        />
      </TableCell>
      <TableCell>
        <StockCell upsell={upsell} onChange={onChange} saving={saving} />
      </TableCell>
    </TableRow>
  );
}

// Orders where the customer paid an upsell charge and the swap never landed,
// read live from Shopify (not the tracking table, which an outage can miss).
// Inside the swap's time limit the reconciler retries them every 10 minutes;
// past it, a person follows up (the order would ship both sets of items).
function UnswappedPanel({ service, store }: { service: PostPurchaseService; store: string }) {
  const [result, setResult] = React.useState<UnswappedResponse | null | undefined>(undefined);
  React.useEffect(() => {
    let current = true;
    setResult(undefined);
    service.getUnswapped(store).then(response => current && setResult(response));
    return () => {
      current = false;
    };
  }, [service, store]);

  const adminUrl = (orderId: number) =>
    result?.domain ? `https://admin.shopify.com/store/${result.domain.replace('.myshopify.com', '')}/orders/${orderId}` : undefined;

  return (
    <Paper variant="outlined">
      <Typography variant="subtitle1" fontWeight={600} sx={{ p: 2, pb: 0 }}>Paid but not swapped</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 1 }}>
        Customers who paid for an upsell whose order still has the replaced items, from Shopify, last 7 days.
        Under 55 minutes old they're retried automatically every 10 minutes. When the swap gives up, the order
        is put on hold in Shopify so ShipBob doesn't ship it yet: swap it by hand, then release the hold.
      </Typography>
      {result === undefined && <LinearProgress aria-label="checking for unswapped orders" />}
      {result === null && <Alert severity="error" sx={{ m: 2 }}>Could not check Shopify for unswapped orders.</Alert>}
      {result && !result.orders.length && (
        <Typography variant="body2" sx={{ px: 2, pb: 2 }}>None — every accepted upsell was swapped.</Typography>
      )}
      {result && result.orders.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="unswapped orders">
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell>
                <TableCell>Placed (Pacific)</TableCell>
                <TableCell>Upsell</TableCell>
                <TableCell align="right">Charged</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.orders.map(order => (
                <TableRow key={`${order.orderId}:${order.slot}`}>
                  <TableCell>
                    <a href={adminUrl(order.orderId)} target="_blank" rel="noreferrer">{order.orderName}</a>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{pacific(order.createdAt.replace('T', ' ').replace('Z', ''))}</TableCell>
                  <TableCell>{order.upsellId ?? order.detail ?? '—'}</TableCell>
                  <TableCell align="right">{order.chargeCents == null ? '—' : usd(order.chargeCents / 100)}</TableCell>
                  <TableCell>
                    {order.fixable ? (
                      <Chip size="small" color="warning" label="Retrying automatically" />
                    ) : (
                      <Chip size="small" color="error" label="Needs a person" />
                    )}
                    {order.onHold && (
                      <Tooltip title="On hold in Shopify, so ShipBob won't ship it yet. Swap it by hand, then release the hold on the order in Shopify.">
                        <Chip size="small" variant="outlined" label="On hold" sx={{ ml: 0.5 }} />
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
}

// What customers see: each upsell's page with today's dosaze.com prices and
// the API's own wording (GET /post-purchase/preview), and its thank-you note.
// Collapsed until opened.
function PreviewPanel({ result }: { result: UpsellPreview[] | null | undefined }) {
  const [open, setOpen] = React.useState(false);
  // One upsell's page at a time; the first until someone picks another.
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1" fontWeight={600}>What customers see</Typography>
        <Button size="small" onClick={() => setOpen(current => !current)} aria-expanded={open}>
          {open ? 'Hide' : 'Show'}
        </Button>
      </Stack>
      {open && (
      <>
      <Alert severity="info" sx={{ my: 1.5 }}>
        The selected upsell's page with today's dosaze.com prices and its exact wording. Shopify draws the real page; this is a
        close copy of its design. To change the wording, prices, products or design, ask Claude in SblyForge to change the
        post-purchase upsell (the post-purchase-upsell skill): it shows the exact page, tests it on the Shopify test
        store and opens a PR for review. Turning an upsell on or off is the switches above, no code.
      </Alert>
      {result === undefined && <LinearProgress aria-label="loading previews" />}
      {result === null && <Alert severity="error">Could not load the previews.</Alert>}
      {result && result.length > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <span id="post-purchase-preview-label" hidden>Upsell</span>
          <Select
            size="small"
            labelId="post-purchase-preview-label"
            name="preview"
            value={selectedId ?? result[0].id}
            onChange={event => setSelectedId(String(event.target.value))}
          >
            {result.map(preview => (
              <MenuItem key={preview.id} value={preview.id}>{preview.id}</MenuItem>
            ))}
          </Select>
        </Stack>
      )}
      {result?.filter(preview => preview.id === (selectedId ?? result[0]?.id)).map(preview => (
        <Box key={preview.id} aria-label={`${preview.id} preview`}>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
            design {preview.designId}
          </Typography>
          {preview.problem ? (
            <Alert severity="warning">This upsell can't be shown right now: {preview.problem}</Alert>
          ) : preview.layout === 'standard' && preview.display ? (
            // Desktop at a checkout's own width beside a phone, so both fit on one screen;
            // the phone wraps below on a narrow one.
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start' }}>
              <Box sx={{ flex: '1 1 560px', maxWidth: 960 }}>
                <Typography variant="caption" color="text.secondary">Desktop</Typography>
                <StandardPagePreview preview={preview} />
              </Box>
              <Box sx={{ width: 390, maxWidth: '100%', flex: 'none' }}>
                <Typography variant="caption" color="text.secondary">Phone</Typography>
                <StandardPagePreview preview={preview} phone />
              </Box>
            </Box>
          ) : (
            <Alert severity="info">
              No dashboard preview for the "{preview.layout}" design yet. See it with npm run preview in
              sbly-dash-collector/shopify-apps/post-purchase-upsells.
            </Alert>
          )}
          {preview.thankYouNote && !preview.problem && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary">Thank-you page note, after accepting</Typography>
              <Alert severity="success">{preview.thankYouNote}</Alert>
            </Box>
          )}
        </Box>
      ))}
      </>
      )}
    </Paper>
  );
}

// Who sees staff-only upsells: one row per person (name, email, customer id),
// each removable, and a form that adds someone by email or customer id. Each
// add or remove is saved at once; the list shown is the one the API saved.
function StaffPanel({ service, staff, onChange, onMessage }: {
  service: PostPurchaseService;
  staff: StaffCustomer[];
  onChange: (staff: StaffCustomer[]) => void;
  onMessage: (message: { severity: 'success' | 'error'; text: string }) => void;
}) {
  const [lookup, setLookup] = React.useState('');
  const [name, setName] = React.useState('');
  const [adding, setAdding] = React.useState(false);
  const [removing, setRemoving] = React.useState<number | null>(null);

  async function add(event: React.FormEvent) {
    event.preventDefault();
    if (!lookup.trim()) return;
    setAdding(true);
    try {
      onChange(await service.addStaff(lookup.trim(), name.trim()));
      onMessage({ severity: 'success', text: `${name.trim() || lookup.trim()} added: they see staff-only upsells within 30 seconds.` });
      setLookup('');
      setName('');
    } catch (error) {
      onMessage({ severity: 'error', text: `Could not add ${lookup.trim()}: ${errorText(error)}` });
    } finally {
      setAdding(false);
    }
  }

  async function remove(person: StaffCustomer) {
    setRemoving(person.customerId);
    try {
      onChange(await service.removeStaff(person.customerId));
      onMessage({ severity: 'success', text: `${person.name ?? person.customerId} removed from the staff list.` });
    } catch (error) {
      onMessage({ severity: 'error', text: `Could not remove ${person.name ?? person.customerId}: ${errorText(error)}` });
    } finally {
      setRemoving(null);
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight={600}>Staff customers</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        dosaze.com customers who see upsells switched to Staff only, for testing on a real checkout. Sign in to that
        customer account on dosaze.com before checking out.
      </Typography>
      {staff.length ? (
        <Table size="small" aria-label="staff customers" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Customer id</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {staff.map(person => (
              <TableRow key={person.customerId}>
                <TableCell>{person.name ?? <Typography variant="body2" color="text.secondary">No name: add them again to name them</Typography>}</TableCell>
                <TableCell>{person.email ?? '—'}</TableCell>
                <TableCell>
                  <a href={`https://admin.shopify.com/store/dosaze/customers/${person.customerId}`} target="_blank" rel="noreferrer">
                    {person.customerId}
                  </a>
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    color="error"
                    onClick={() => remove(person)}
                    disabled={removing != null || adding}
                    aria-label={`remove ${person.name ?? person.customerId}`}
                  >
                    {removing === person.customerId ? 'Removing…' : 'Remove'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" sx={{ mb: 2 }}>
          No staff customers yet, so an upsell switched to Staff only shows to nobody.
        </Typography>
      )}
      <form onSubmit={add}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            size="small"
            label="Email or customer id"
            value={lookup}
            onChange={event => setLookup(event.target.value)}
            placeholder="name@sbly.com"
            sx={{ flex: 2 }}
            inputProps={{ 'aria-label': 'staff email or customer id' }}
          />
          <TextField
            size="small"
            label="Name"
            value={name}
            onChange={event => setName(event.target.value)}
            placeholder="Who is this?"
            sx={{ flex: 1 }}
            inputProps={{ 'aria-label': 'staff name', maxLength: 60 }}
          />
          <Button type="submit" variant="contained" disabled={adding || removing != null || !lookup.trim()}>
            {adding ? 'Adding…' : 'Add'}
          </Button>
        </Stack>
      </form>
      <Typography variant="caption" color="text.secondary">
        Found among dosaze.com's customers, so they need an account or an order there. Adding someone already listed
        updates their name.
      </Typography>
    </Paper>
  );
}

export default function PostPurchasePage() {
  const service = React.useMemo(() => new PostPurchaseService(), []);
  const [period, setPeriod] = React.useState<Period>({ days: 30 });
  const [store, setStore] = React.useState('US');
  const [data, setData] = React.useState<UpsellsResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState<{ severity: 'success' | 'error'; text: string } | null>(null);
  const { confirm, dialog: confirmDialog } = useConfirm();
  const [tab, setTab] = React.useState<'upsells' | 'tests' | 'shipping' | 'orders'>('upsells');
  // Every upsell's page (GET /preview), for "What customers see". undefined
  // while loading, null on failure.
  const [previews, setPreviews] = React.useState<UpsellPreview[] | null | undefined>(undefined);
  React.useEffect(() => {
    let current = true;
    service.getPreview().then(response => current && setPreviews(response ? response.upsells : null));
    return () => {
      current = false;
    };
  }, [service]);

  // Only the latest request may land: switching store or period while one is
  // in flight must not let the older answer overwrite the newer.
  const fetchIdRef = React.useRef(0);

  const load = React.useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    const response = await service.getUpsells(period, store);
    if (fetchId !== fetchIdRef.current) return;
    setLoading(false);
    setLoadFailed(!response);
    if (!response) {
      // Keep what is on screen only if it is this selection's (e.g. the reload
      // after a save failed); another store's numbers would be mislabelled.
      setData(current => (current?.store === store && samePeriod(current, period) ? current : null));
      return;
    }
    setData(response);
  }, [service, period, store]);

  React.useEffect(() => {
    load();
  }, [load]);

  async function saveSwitches(upsell: Upsell, switches: Switches) {
    // Staff only off is the one change that can go live: the API applies it to
    // whatever On it holds now, which this page may not have seen yet.
    const goesLive = switches.staffOnly === false;
    if (
      goesLive &&
      !(await confirm({
        title: `Show ${upsell.id} to every customer?`,
        body: 'Every qualifying customer on dosaze.com sees this upsell, and accepting it charges their card. Checkouts follow within 30 seconds.',
        confirmLabel: 'Go live',
        tone: 'warning',
      }))
    ) {
      return;
    }
    setSaving(true);
    try {
      await service.setSwitches(upsell.id, switches);
      setMessage({ severity: 'success', text: `${upsell.id} updated — live checkouts follow within 30 seconds.` });
      await load();
    } catch (error) {
      setMessage({ severity: 'error', text: `Could not update ${upsell.id}: ${errorText(error)} Nothing changed.` });
    } finally {
      setSaving(false);
    }
  }

  // Every A/B test, across upsells, for the A/B tests tab.
  const experimentCount = (data?.upsells ?? []).reduce((sum, upsell) => sum + (upsell.experiments?.length ?? 0), 0);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h5" fontWeight={700}>Post-Purchase Upsells</Typography>
        {/* Hidden labels: labelId names each select "<label> <value>" (aria-label would hide the value). */}
        <span id="post-purchase-store-label" hidden>Store</span>
        <span id="post-purchase-period-label" hidden>Period</span>
        <Stack direction="row" spacing={1}>
          <Select size="small" value={store} onChange={event => setStore(String(event.target.value))} labelId="post-purchase-store-label" name="store">
            {(data?.stores ?? ['US']).map(id => (
              <MenuItem key={id} value={id}>{STORE_LABELS[id] ?? id}</MenuItem>
            ))}
          </Select>
          <PeriodPicker period={period} onChange={setPeriod} />
        </Stack>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Offers shown on dosaze.com between payment and the thank-you page. Products, prices, wording and
        design are defined in code; switch them on here. A checkout sees the first live upsell it qualifies
        for{data && data.maxOffersPerCheckout > 1 ? ` (up to ${data.maxOffersPerCheckout})` : ''}.
      </Typography>

      {loading && !data && <PageSkeleton />}
      {loading && data && <LinearProgress aria-label="loading upsells" sx={{ mb: 2 }} />}
      {loadFailed && <Alert severity="error" sx={{ mb: 2 }}>Could not load the upsells.</Alert>}

      {data?.store === 'DEV' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing the test store. Every upsell is always on there — the switches below control dosaze.com only.
        </Alert>
      )}

      {data && (
        <>
          <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Tab value="upsells" label="Upsells" />
            <Tab value="tests" label={`A/B tests (${experimentCount})`} />
            <Tab value="shipping" label="Shipping & margin" />
            <Tab value="orders" label="Orders" />
          </Tabs>
          {tab === 'upsells' && (
            <Stack spacing={3}>
          <RevenueChart data={data} />
          <Paper variant="outlined">
            <Table size="small" aria-label="upsells">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Upsell</TableCell>
                  <TableCell>Design</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>On</TableCell>
                  <TableCell>
                    <Tooltip title="Only the Shopify customers in the staff list see it — for testing on a real checkout.">
                      <span>Staff only</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      title={
                        'Offer only when a ShipBob center can send the upgrade. Any US center (the default): anywhere ' +
                        'in the US. Nearest center: the center nearest the customer that stocks their order has it, so ' +
                        'it never ships from farther away. Same region: any center in their region (West, Central, East). ' +
                        'Same center (strictest): the one center that holds everything on their order has it too, so the ' +
                        'order still ships as one box from where it would have.'
                      }
                    >
                      <span>Stock check</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.upsells.map(upsell => (
                  <UpsellRow key={upsell.id} upsell={upsell} saving={saving} onChange={switches => saveSwitches(upsell, switches)} />
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Paper variant="outlined">
            <Typography variant="subtitle1" fontWeight={600} sx={{ p: 2, pb: 0 }}>Performance by design</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
              Take rate = accepted ÷ viewed. Upsell revenue is the extra charged, before tax. Click a design to see the orders that took it, and any that went wrong.
            </Typography>
            <PerformanceTable stats={data.stats} upsells={data.upsells} service={service} period={{ from: data.from, to: data.to }} store={data.store} />
          </Paper>

          <PreviewPanel result={previews} />

          <UnswappedPanel service={service} store={store} />

          <StaffPanel
            service={service}
            staff={data.staff}
            onChange={staff => setData(current => (current ? { ...current, staff } : current))}
            onMessage={setMessage}
          />

            </Stack>
          )}
          {tab === 'tests' && (
            <ExperimentsTab service={service} upsells={data.upsells} confirm={confirm} onChanged={load} onMessage={setMessage} />
          )}
          {tab === 'shipping' && <ShippingTab service={service} period={{ from: data.from, to: data.to }} store={data.store} />}
          {tab === 'orders' && <OrdersTab service={service} />}
        </>
      )}

      {confirmDialog}
      <Snackbar open={!!message} autoHideDuration={5000} onClose={() => setMessage(null)}>
        {message ? <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert> : <span />}
      </Snackbar>
    </Box>
  );
}
